import React, { useEffect, useCallback, useRef } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Login from "../pages/Login";
import ForgotPassword from "../pages/forgotPassword";
import ResetPassword from "../pages/resetPassword";
import ActivateAccount from "../pages/activateAccount";
import Home from "../pages/dashboard/Home";
import Users from "../pages/dashboard/Users";
import Report from "../pages/dashboard/Report";
import PatientInfo from "../pages/dashboard/PatientInfo";
import NotFound from "../pages/NotFound"
import { refreshToken } from "../redux/actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import PrivateRoute from "../components/PrivateRoute";
import RoleBasedRoute from "../components/RoleBasedRoute";
import { createActivityTracker } from "../utils/activityTracker";

// Token expiration threshold (30 seconds before expiration)
const TOKEN_EXPIRATION_THRESHOLD = 30 * 1000; // 30 seconds in milliseconds

function WebRoutes() {
  const dispatch = useDispatch();
  const { refresh_token, token_expire_time, auth_token } = useSelector((state) => state.auth);
  const activityTrackerRef = useRef(null);
  const lastRefreshAttemptRef = useRef(0);
  
  // Check if token is close to expiring
  const isTokenCloseToExpiring = useCallback(() => {
    if (!token_expire_time) {
      // Try to get from sessionStorage
      const expireTimeStr = sessionStorage.getItem('token_expire_time');
      if (!expireTimeStr) {
        return false;
      }
      const expireTime = parseInt(expireTimeStr, 10);
      const timeUntilExpiry = expireTime - Date.now();
      return timeUntilExpiry > 0 && timeUntilExpiry <= TOKEN_EXPIRATION_THRESHOLD;
    }
    
    const timeUntilExpiry = token_expire_time - Date.now();
    return timeUntilExpiry > 0 && timeUntilExpiry <= TOKEN_EXPIRATION_THRESHOLD;
  }, [token_expire_time]);

  const refreshAuthToken = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    const now = Date.now();
    if (now - lastRefreshAttemptRef.current < 5000) {
      return; // Too soon since last attempt
    }
    
    lastRefreshAttemptRef.current = now;
    
    try {
      await dispatch(refreshToken());
    } catch (error) {
      // Error handling is done in the action
    }
  }, [dispatch]);

  // Handle activity-based token refresh
  const handleActivity = useCallback(() => {
    // Only refresh if token exists and is close to expiring
    if (auth_token && refresh_token && isTokenCloseToExpiring()) {
      refreshAuthToken();
    }
  }, [auth_token, refresh_token, isTokenCloseToExpiring, refreshAuthToken]);

  // Set up activity tracking
  useEffect(() => {
    if (auth_token && refresh_token) {
      // Create and start activity tracker
      activityTrackerRef.current = createActivityTracker(handleActivity, 5000);
      activityTrackerRef.current.start();

      return () => {
        if (activityTrackerRef.current) {
          activityTrackerRef.current.stop();
          activityTrackerRef.current = null;
        }
      };
    }
  }, [auth_token, refresh_token, handleActivity]);

  // Also check token expiration periodically (every 10 seconds) as a fallback
  useEffect(() => {
    if (!auth_token || !refresh_token) {
      return;
    }

    const checkInterval = setInterval(() => {
      if (isTokenCloseToExpiring()) {
        // Only refresh if user has been active recently (within last 2 minutes)
        // This prevents refresh when user is away
        const lastActivity = lastRefreshAttemptRef.current;
        const timeSinceLastActivity = Date.now() - lastActivity;
        if (timeSinceLastActivity < 2 * 60 * 1000) {
          refreshAuthToken();
        }
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(checkInterval);
    };
  }, [auth_token, refresh_token, isTokenCloseToExpiring, refreshAuthToken]);


    return (
        <Routes>
          <Route path="/notfound" element={<NotFound />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account/activate/:activatekey" element={<ActivateAccount />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/account/reset-password/:code" element={<ResetPassword />} />
          <Route path="/" element={<PrivateRoute><Report /></PrivateRoute>} />
          <Route path="/report" element={<PrivateRoute><Report /></PrivateRoute>} />
          <Route path="/users" element={<RoleBasedRoute requiredRole="ROLE_ADMIN"><Users /></RoleBasedRoute>} />
          <Route path="*" element={<Navigate to="/report" />} />
        </Routes>
      );
}

export default WebRoutes;